import { motion } from "framer-motion";
import { Smartphone, Globe, HeartPulse, GraduationCap, Sparkles, ArrowUpRight } from "lucide-react";
import { useLanguage, IMG_SVC_MOBILE, IMG_SVC_WEB, IMG_SVC_AI, IMG_SVC_MEDICAL, IMG_SVC_EDUCATION } from "@/i18n";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const AiBadge = ({ label, dark = false }) => (
  <span
    className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-full ${
      dark ? "bg-white/15 text-indigo-100 border border-white/20" : "bg-indigo-50 text-indigo-600 border border-indigo-100"
    }`}
  >
    <Sparkles className="h-3 w-3" />
    {label}
  </span>
);

const ServiceCard = ({ icon: Icon, title, desc, image, aiBadge, delay, testid }) => (
  <motion.div
    {...fadeUp(delay)}
    data-testid={testid}
    className="group h-full flex flex-col overflow-hidden bg-white border border-slate-200 rounded-3xl shadow-sm hover:-translate-y-1.5 hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-100/50 transition-all duration-300"
  >
    <div className="relative h-48 overflow-hidden bg-slate-50">
      <img
        src={image}
        alt={title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute top-4 left-4">
        <AiBadge label={aiBadge} />
      </div>
    </div>
    <div className="p-7 flex flex-col flex-1">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {title}
        </h3>
      </div>
      <p className="mt-4 text-slate-600 leading-relaxed">{desc}</p>
    </div>
  </motion.div>
);

export const Services = () => {
  const { t } = useLanguage();
  const s = t.services;

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...fadeUp(0)} className="max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">
            <Sparkles className="h-4 w-4" />
            {s.overline}
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {s.title}
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">{s.subtitle}</p>
        </motion.div>

        {/* Featured AI card */}
        <motion.div
          {...fadeUp(0.05)}
          data-testid="service-ai"
          className="mt-14 relative overflow-hidden rounded-3xl bg-slate-900 text-white border border-slate-800 grid md:grid-cols-2 gap-8 md:gap-0"
        >
          <div className="absolute -top-24 -right-10 h-72 w-72 rounded-full bg-indigo-600/30 blur-3xl" />
          <div className="relative p-8 md:p-12 flex flex-col justify-center">
            <AiBadge label={s.aiBadge} dark />
            <h3
              className="mt-5 text-2xl md:text-3xl font-black tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {s.ai.title}
            </h3>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-md">{s.ai.desc}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {s.ai.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-indigo-100"
                >
                  <ArrowUpRight className="h-3.5 w-3.5" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <div className="relative min-h-[260px] md:min-h-full">
            <img src={IMG_SVC_AI} alt={s.ai.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/40 to-transparent md:from-slate-900/80 md:via-transparent" />
          </div>
        </motion.div>

        {/* Other services */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <ServiceCard
            testid="service-mobile"
            icon={Smartphone}
            title={s.mobile.title}
            desc={s.mobile.desc}
            image={IMG_SVC_MOBILE}
            aiBadge={s.aiBadge}
            delay={0.05}
          />
          <ServiceCard
            testid="service-web"
            icon={Globe}
            title={s.web.title}
            desc={s.web.desc}
            image={IMG_SVC_WEB}
            aiBadge={s.aiBadge}
            delay={0.1}
          />
          <ServiceCard
            testid="service-medical"
            icon={HeartPulse}
            title={s.medical.title}
            desc={s.medical.desc}
            image={IMG_SVC_MEDICAL}
            aiBadge={s.aiBadge}
            delay={0.15}
          />
          <ServiceCard
            testid="service-education"
            icon={GraduationCap}
            title={s.education.title}
            desc={s.education.desc}
            image={IMG_SVC_EDUCATION}
            aiBadge={s.aiBadge}
            delay={0.2}
          />
        </div>
      </div>
    </section>
  );
};
